import { ContentTypeFieldResponse } from '@cs-cms/types';
import {
  useCurrentUserContext,
  useLoadFieldValues,
  useGlobalizationContext,
  useGetContentType,
  useLoadFieldValuesFromFile
} from '@cs-cms/state-management';
import { resolveFieldChangeHandler } from './fieldChangeHandlerResolver';
import type {
  FieldInteractionCallbacks,
  FieldInteractionsDependentFieldsClearedContext,
  FieldInteractionsLookupFilledContext,
  UseFieldInteractionsProps,
  UseFieldInteractionsReturn
} from './fieldInteractionsTypes';
import { useFieldChangeHandlers } from './useFieldChangeHandlers';
import { useFieldInteractionApi } from './useFieldInteractionApi';
import { useFieldState } from './useFieldState';

export type {
  FieldInteractionCallbacks,
  FieldInteractionsDependentFieldsClearedContext,
  FieldInteractionsLookupFilledContext,
  UseFieldInteractionsProps,
  UseFieldInteractionsReturn
};

export const useFieldInteractions = ({
  ctFields,
  contentTypeFields: contentTypeFieldsFromProps,
  selectedCtName,
  fieldInteractionCallbacks
}: UseFieldInteractionsProps): UseFieldInteractionsReturn => {
  const contentTypeFields = contentTypeFieldsFromProps ?? ctFields;
  const fieldState = useFieldState();
  const { selectedIndex } = useCurrentUserContext();
  const { mutateAsync: loadFieldValues } = useLoadFieldValues();
  const { selectedLocale } = useGlobalizationContext();
  const { data: contentType } = useGetContentType(selectedCtName, selectedLocale);
  const { mutateAsync: loadFieldValuesFromFile } = useLoadFieldValuesFromFile();

  const api = useFieldInteractionApi({
    ctFields,
    contentTypeFields,
    selectedCtName,
    selectedIndexId: selectedIndex?.id,
    fieldState,
    loadFieldValues,
    fieldInteractionCallbacks
  });

  const handlers = useFieldChangeHandlers({
    ctFields,
    selectedCtName,
    fieldState,
    api,
    contentTypeUsesFilesProcessor: contentType?.usesFilesProcessor,
    loadFieldValuesFromFile
  });

  const getFieldChangeHandler = (field: ContentTypeFieldResponse) =>
    resolveFieldChangeHandler(field.type, handlers.fieldHandlers);

  const getFieldChoices = (field: ContentTypeFieldResponse): string[] => {
    if (field.usesExtDictionary && fieldState.dynamicChoices[field.name]) {
      return fieldState.dynamicChoices[field.name];
    }
    return field.choices || [];
  };

  const isFieldEnabled = (field: ContentTypeFieldResponse): boolean => {
    if (!field.dependsOnFields?.length) {
      return true;
    }

    return fieldState.enabledFields.has(field.name);
  };

  return {
    getFieldChangeHandler,
    getChoiceFilterHandler: handlers.getChoiceFilterHandler,
    getManualSearchHandler: handlers.getManualSearchHandler,
    getRefreshHandler: handlers.getRefreshHandler,
    loadingFields: fieldState.loadingFields,
    dynamicChoices: fieldState.dynamicChoices,
    getFieldChoices,
    handleFileFieldChange: handlers.handleFileFieldChange,
    isFieldEnabled,
    runLookupForField: api.runLookupForField
  };
};
